import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Box,
    TextField,
    InputAdornment,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    Button,
    TablePagination,
    IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from 'react-toastify';
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../api/httpClient";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

function ParalegalList() {
    const navigate = useNavigate();
    const [paralegals, setParalegals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [searchQuery, setSearchQuery] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");

    // Fetch paralegals from the API
    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await apiClient("/legal-service-paralegals/", "GET");
            setParalegals(response || []);
        } catch (error) {
            console.error("Error fetching paralegals:", error);
            toast.error("Failed to load paralegals.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (paralegal) => {
        const newStatus = !paralegal.is_active;
        const previousParalegals = [...paralegals];

        // Optimistic update
        setParalegals((prev) =>
            prev.map((p) =>
                p.lawfirm_user_id === paralegal.lawfirm_user_id
                    ? { ...p, is_active: newStatus }
                    : p
            )
        );

        try {
            const res = await apiClient(
                `/update-lawfirm-user/${paralegal.lawfirm_user_id}`,
                "PUT",
                { is_active: newStatus }
            );
            if (res) {
                toast.success(`User set to ${newStatus ? "Active" : "Inactive"}`);
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("Failed to update status. Reverting changes.");
            setParalegals(previousParalegals);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Filter paralegals based on the search query and active status
    const filteredParalegals = paralegals.filter((paralegal) => {
        const name = paralegal.user?.username || paralegal.user?.email || "";
        const matchesName = name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesActive =
            activeFilter === "all" ||
            (activeFilter === "active" && paralegal.is_active) ||
            (activeFilter === "inactive" && !paralegal.is_active);

        return matchesName && matchesActive;
    });

    useEffect(() => {
        setPage(0);
    }, [searchQuery, activeFilter]);

    // Handle page change
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedParalegals = filteredParalegals.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Navigate to Paralegal Detail
    const handleViewParalegal = (id) => {
        navigate(`/vermost_paralegal_detail/${id}`);
    };

    return (
        <>
            <Navbar />

            <Box sx={{ maxWidth: "100%", padding: "20px" }}>
                {/* Heading */}
                <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
                    Legal Service Paralegals
                </Typography>

                {/* Search bar and Active filter */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px",
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <TextField
                            label="Search by Name/Email"
                            variant="outlined"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                width: "300px",
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                },
                            }}
                            slotProps={{ input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}}
                        />
                        <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={activeFilter}
                                onChange={(e) => setActiveFilter(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/vermost_create_paralegal")}
                        sx={{ height: "56px" }}
                    >
                        NEW PARALEGAL
                    </Button>
                </Box>

                {/* Loader or Table */}
                {loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="60vh"
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ width: "100%", overflowX: "auto", mb: 2, borderRadius: "8px" }}>
                        <Table>
                            <TableHead>
                                <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Active</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedParalegals.length > 0 ? (
                                    paginatedParalegals.map((paralegal) => (
                                        <TableRow key={paralegal.lawfirm_user_id} hover>
                                            <TableCell>
                                                <Typography>
                                                    {paralegal.user?.first_name} {paralegal.user?.last_name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {paralegal.firm_email
                                                    ? `${paralegal.firm_email} (Firm)`
                                                    : paralegal.user?.email || "N/A"}
                                            </TableCell>
                                            <TableCell>
                                                <Switch
                                                    checked={paralegal.is_active}
                                                    onChange={() => handleToggleActive(paralegal)}
                                                    size="small"
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleViewParalegal(paralegal.lawfirm_user_id)}
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                            {loading ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                <Typography color="textSecondary">
                                                    No paralegals found.
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Pagination */}
                <TablePagination
          ActionsComponent={TablePaginationActions}
                    component="div"
                    count={filteredParalegals.length}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Box>
        </>
    );
}

export default ParalegalList;
